import { useQueryClient } from '@tanstack/react-query'
import { type CommandKind, type CommandPayload, ProviderAccountMetadata } from '@viko/protocol'
import { Button } from '@viko/transcript-ui'
import { useEffect, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { accountStatus } from '../lib/provider-accounts.js'
import { qk } from '../lib/queries.js'
import { isActiveTab } from '../lib/query-client.js'
import type { Machine } from '../lib/types.js'
import { errorText } from './ui.js'

/** Credentials stay on the selected machine; every mutation uses the signed dispatch hook. */
export function ProviderAccounts({ machine }: { machine: Machine }) {
  const b = useBackend()
  const qc = useQueryClient()
  const [provider, setProvider] = useState<'claude' | 'codex'>('claude')
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [confirmRemove, setConfirmRemove] = useState<string>()
  // claude-accounts: while a browser sign-in is pending on the machine, refresh readiness live.
  const pending = machine.providerAccounts?.some((a) => a.loginPending) ?? false
  useEffect(() => {
    if (!pending) return
    const t = setInterval(() => {
      if (isActiveTab())
        void qc.invalidateQueries({ queryKey: qk.machines }, { cancelRefetch: false })
    }, 5_000)
    return () => clearInterval(t)
  }, [pending, qc])
  const act = async <K extends CommandKind>(kind: K, payload: CommandPayload<K>) => {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const outcome = await b.dispatch(kind, machine.id, payload)
      const result = outcome.result as { account?: unknown } | undefined
      const parsed = ProviderAccountMetadata.safeParse(result?.account)
      if (parsed.success) {
        qc.setQueryData<Machine[]>(qk.machines, (rows) =>
          rows?.map((m) =>
            m.id === machine.id
              ? {
                  ...m,
                  providerAccounts: [
                    ...(m.providerAccounts ?? []).filter((a) => a.id !== parsed.data.id),
                    parsed.data,
                  ],
                }
              : m,
          ),
        )
        if (kind === 'account.add') setLabel('')
        if (kind === 'account.login' && (result as { started?: boolean }).started)
          setNotice(
            `Finish signing in in the browser that opened on ${machine.name}. This updates by itself.`,
          )
      } else if (kind === 'account.remove') {
        const removed = (result as { removed?: string } | undefined)?.removed
        qc.setQueryData<Machine[]>(qk.machines, (rows) =>
          rows?.map((m) =>
            m.id === machine.id
              ? { ...m, providerAccounts: m.providerAccounts?.filter((a) => a.id !== removed) }
              : m,
          ),
        )
        setConfirmRemove(undefined)
      } else setNotice('Request sent. Account details will update when the machine responds.')
      await qc.invalidateQueries({ queryKey: qk.machines })
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }
  if (!machine.providerAccounts)
    return (
      <p className="m-0 text-[14px] text-text-3">
        {b.content && machine.reportAt === undefined
          ? `Waiting for ${machine.name} to send its encrypted machine report. Existing CLI logins remain available.`
          : 'Update Viko on this machine to manage provider accounts. Existing CLI logins remain available.'}
      </p>
    )
  const disabled = busy || !machine.online
  return (
    <details>
      <summary className="cursor-pointer text-[14px] font-medium text-text-2">
        Provider accounts
      </summary>
      <div className="mt-3 grid gap-3">
        <p className="m-0 text-[14px] text-text-3">
          Optional. Use existing CLI logins or add separate accounts on {machine.name}. Use labels
          such as Work or Personal; avoid email addresses.
        </p>
        {machine.providerAccounts.map((account) => (
          <div className="grid gap-2 rounded-lg border border-border-subtle p-3" key={account.id}>
            <p className="m-0 text-[14px]">
              <strong>
                {account.provider === 'claude' ? 'Claude' : 'Codex'} · {account.label}
              </strong>{' '}
              — {accountStatus(account)}
            </p>
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                const value = new FormData(e.currentTarget).get('label')
                if (typeof value === 'string')
                  void act('account.rename', { accountId: account.id, label: value })
              }}
            >
              <input
                key={account.label}
                name="label"
                className="input min-w-0 flex-1"
                aria-label={`Label for ${account.label}`}
                defaultValue={account.label}
                maxLength={120}
                required
                disabled={disabled}
              />
              <Button size="sm" type="submit" disabled={disabled}>
                Rename
              </Button>
            </form>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={disabled}
                onClick={() =>
                  void act('account.enable', { accountId: account.id, enabled: !account.enabled })
                }
              >
                {account.enabled ? 'Disable' : 'Enable'}
              </Button>
              {account.mode === 'isolated' && account.enabled ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={disabled}
                  onClick={() => void act('account.status', { accountId: account.id })}
                >
                  Check readiness
                </Button>
              ) : null}
              {/* ---- claude-accounts: sign in on the machine, cancel, remove ---- */}
              {account.mode === 'isolated' &&
              account.enabled &&
              account.readiness !== 'ready' &&
              !account.loginPending ? (
                <Button
                  size="sm"
                  variant="primary"
                  disabled={disabled}
                  onClick={() => void act('account.login', { accountId: account.id })}
                >
                  Sign in on {machine.name}
                </Button>
              ) : null}
              {account.loginPending ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={disabled}
                  onClick={() => void act('account.login', { accountId: account.id, cancel: true })}
                >
                  Cancel sign-in
                </Button>
              ) : null}
              {account.mode === 'isolated' ? (
                confirmRemove === account.id ? (
                  <>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={disabled}
                      onClick={() => void act('account.remove', { accountId: account.id })}
                    >
                      Confirm remove
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmRemove(undefined)}>
                      Keep
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={disabled || (account.activeRuns ?? 0) > 0}
                    onClick={() => setConfirmRemove(account.id)}
                  >
                    Remove
                  </Button>
                )
              ) : null}
            </div>
            {confirmRemove === account.id ? (
              <p className="m-0 text-[14px] text-text-2">
                Removes {account.label} from Viko on {machine.name}. Its sign-in files stay on that
                machine (moved aside), so nothing is uploaded or lost.
              </p>
            ) : null}
            {account.loginPending ? (
              <p role="status" className="m-0 text-[14px] text-text-2">
                Signing in — complete it in the browser on {machine.name}.
              </p>
            ) : null}
            {account.mode === 'isolated' &&
            account.enabled &&
            account.readiness !== 'ready' &&
            !account.loginPending ? (
              <div className="grid gap-1 text-[14px] text-text-2">
                <span>
                  If no browser opens on {machine.name}, run this there instead, then check
                  readiness:
                </span>
                <code className="select-all break-all rounded bg-surface-2 p-2">
                  viko account login {account.id}
                </code>
              </div>
            ) : null}
            {/* ---- end claude-accounts ---- */}
            {account.checkedAt ? (
              <span className="text-[14px] text-text-3">
                Last checked {new Date(account.checkedAt).toLocaleString()}
              </span>
            ) : null}
          </div>
        ))}
        <form
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            void act('account.add', { provider, label: label.trim() })
          }}
        >
          <label className="field">
            <span>Provider</span>
            <select
              className="input"
              value={provider}
              disabled={disabled}
              onChange={(e) => setProvider(e.target.value as 'claude' | 'codex')}
            >
              <option value="claude">Claude</option>
              <option value="codex">Codex</option>
            </select>
          </label>
          <label className="field">
            <span>Account label</span>
            <input
              className="input"
              value={label}
              maxLength={120}
              placeholder="Work"
              required
              disabled={disabled}
              onChange={(e) => setLabel(e.target.value)}
            />
          </label>
          <Button variant="primary" type="submit" disabled={disabled || !label.trim()}>
            Add account
          </Button>
        </form>
        {!machine.online ? (
          <p className="m-0 text-[14px] text-text-3">Connect this machine to manage accounts.</p>
        ) : null}
        {error ? (
          <p role="alert" className="m-0 text-[14px] text-status-fail">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p role="status" className="m-0 text-[14px] text-text-2">
            {notice}
          </p>
        ) : null}
      </div>
    </details>
  )
}
